import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAgent } from "@/hooks/useAgentDetail";
import { Clock, Eye } from "lucide-react";
import { safeFormatDistanceToNow } from "@/utils/dateUtils";
import { OverviewTab } from "@/components/security/agent-detail/OverviewTab";
import { IssuesTab } from "@/components/security/agent-detail/IssuesTab";
import { TracesTab } from "@/components/security/agent-detail/TracesTab";
import { MetricsTab } from "@/components/security/agent-detail/MetricsTab";
import { ConfigurationTab } from "@/components/security/agent-detail/ConfigurationTab";
import { DriftTab } from "@/components/security/agent-detail/DriftTab";
import { AgentProviderLogo } from "@/components/security/AgentProviderLogo";
import type { MetricsWindow, StatsTimeWindow } from "@/services/aiAgentsService";

const TABS = ["overview", "issues", "traces", "metrics", "drift", "configuration"] as const;
type TabId = (typeof TABS)[number];

const TAB_LABELS: Record<TabId, string> = {
  overview: "Overview",
  issues: "Security Issues",
  traces: "Traces",
  metrics: "Metrics",
  drift: "Drift Detection",
  configuration: "Configuration",
};

export function AgentDetailPage() {
  const { agentId } = useParams<{ agentId: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const tabParam = searchParams.get("tab") as TabId | null;
  const [activeTab, setActiveTab] = useState<TabId>(
    tabParam && TABS.includes(tabParam) ? tabParam : "overview"
  );
  const [overviewWindow, setOverviewWindow] = useState<StatsTimeWindow>("30");
  const [metricsWindow, setMetricsWindow] = useState<MetricsWindow>("7d");

  useEffect(() => {
    if (activeTab !== "overview") {
      setSearchParams({ tab: activeTab }, { replace: true });
    } else {
      setSearchParams({}, { replace: true });
    }
  }, [activeTab, setSearchParams]);

  const numericId = agentId ? parseInt(agentId, 10) : 0;
  const { data: agent, isLoading, error } = useAgent(numericId);

  if (isLoading) {
    return (
      <div className="flex flex-1 flex-col gap-6 bg-muted/10 px-4 py-6 lg:px-6">
        <Skeleton className="h-9 w-36 rounded-full" />
        <div className="flex items-center gap-4">
          <Skeleton className="h-12 w-12 rounded-[15px]" />
          <div className="space-y-2"><Skeleton className="h-7 w-64" /><Skeleton className="h-4 w-44" /></div>
        </div>
        <Skeleton className="h-14 w-full rounded-[15px]" />
        <Skeleton className="h-72 w-full rounded-[15px]" />
      </div>
    );
  }

  if (error || !agent) {
    return (
      <div className="flex flex-1 items-center justify-center p-6">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-2">Agent Not Found</h2>
          <p className="text-muted-foreground mb-4">
            {error instanceof Error ? error.message : "The requested agent could not be found."}
          </p>
          <Button className="rounded-full" onClick={() => navigate("/agents")}>Back to Agents</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-1 flex-col gap-6 bg-muted/10 px-4 py-6 lg:px-6">
      {/* Header — icon box + title + metadata */}
      <div className="flex flex-wrap items-start gap-4">
        <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-[15px] border bg-white">
          <AgentProviderLogo provider={agent.model_provider} className="h-7 w-7" />
        </div>
        <div className="min-w-0 flex-1">
          <h1 className="mb-1 text-2xl font-semibold tracking-tight">{agent.name}</h1>
          <div className="text-sm text-muted-foreground mb-3">
            {agent.trace_source} &middot; {agent.model_name}
          </div>
          <div className="flex w-full flex-wrap items-center gap-2">
            <Badge variant="outline" className="rounded-[5px] bg-white text-xs font-medium">
              {agent.model_name}
            </Badge>
            {agent.model_provider && (
              <Badge className="rounded-[5px] border border-[#00B0E4]/30 bg-[#00B0E4]/10 text-xs text-[#007fa5]">
                {agent.model_provider}
              </Badge>
            )}
            <span className="text-xs text-muted-foreground flex items-center gap-1 ml-2">
              <Eye className="h-3 w-3" />
              First seen {safeFormatDistanceToNow(agent.first_seen_at, { addSuffix: true })}
            </span>
            <span className="text-xs text-muted-foreground flex items-center gap-1">
              <Clock className="h-3 w-3" />
              Active {safeFormatDistanceToNow(agent.last_seen_at, { addSuffix: true })}
            </span>
            <div className="ml-auto">
              {activeTab === "overview" && (
                <Select value={overviewWindow} onValueChange={(value) => setOverviewWindow(value as StatsTimeWindow)}>
                  <SelectTrigger className="h-9 w-[140px] rounded-full bg-white text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent className="rounded-[20px] bg-white">
                    <SelectItem value="7">Last 7 days</SelectItem>
                    <SelectItem value="30">Last 30 days</SelectItem>
                    <SelectItem value="90">Last 90 days</SelectItem>
                  </SelectContent>
                </Select>
              )}
              {activeTab === "metrics" && (
                <Select value={metricsWindow} onValueChange={(value) => setMetricsWindow(value as MetricsWindow)}>
                  <SelectTrigger className="h-9 w-[160px] rounded-full bg-white text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent className="rounded-[20px] bg-white">
                    <SelectItem value="1h">Last 1 hour</SelectItem>
                    <SelectItem value="6h">Last 6 hours</SelectItem>
                    <SelectItem value="24h">Last 24 hours</SelectItem>
                    <SelectItem value="7d">Last 7 days</SelectItem>
                    <SelectItem value="30d">Last 30 days</SelectItem>
                    <SelectItem value="90d">Last 90 days</SelectItem>
                  </SelectContent>
                </Select>
              )}
              {activeTab !== "overview" && activeTab !== "metrics" && (
                <div className="h-9 w-[160px]" aria-hidden="true" />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Tab bar — exact IssueDetailPage pattern */}
      <div className="w-full">
        <div className="flex items-center gap-1 overflow-x-auto rounded-[15px] border bg-white p-1.5">
          {TABS.map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`whitespace-nowrap rounded-[10px] px-4 py-2.5 text-sm font-medium transition-colors ${
                activeTab === tab
                  ? "bg-muted text-foreground"
                  : "text-muted-foreground hover:bg-muted/50 hover:text-foreground"
              }`}
            >
              {TAB_LABELS[tab]}
            </button>
          ))}
        </div>

        {activeTab === "overview" && <OverviewTab agent={agent} timeWindow={overviewWindow} />}
        {activeTab === "issues" && <IssuesTab agentId={numericId} />}
        {activeTab === "traces" && <TracesTab agentId={numericId} />}
        {activeTab === "metrics" && <MetricsTab agentId={numericId} timeWindow={metricsWindow} />}
        {activeTab === "drift" && <DriftTab agentId={numericId} />}
        {activeTab === "configuration" && <ConfigurationTab agent={agent} agentId={numericId} />}
      </div>
    </div>
  );
}
